import { useState, useEffect } from 'react';
import Loader from '../../../components/globalloaders/Loader';
import { toast } from 'react-toastify';
import { Bars3Icon } from "@heroicons/react/24/outline";
import { decryptPayload, formatLabel, formatTableDateTime, formatCurrency } from '../../../constants/helpers';
import api from '../../../utils/auth/api';

const History = ({ setIsMobileOpen }) => {

    const [loaders, setLoaders] = useState({
        fetchHistory: false,
    });

    // Main Mode-Control & Data-Holding States
    const [pageMode, setPageMode] = useState("entry");
    const [historyData, setHistoryData] = useState([]);

    // Fetch API
    useEffect(() => {
        const historyEndpoints = {
            entry: "/entry/rider-history",
            serviceFee: "/finance/rider-servicefee-history",
            expense: "/finance/rider-expense-history",
        };
        const fetchData = async () => {
            setHistoryData([]);
            setLoaders(prev => ({ ...prev, fetchHistory: true }));
            try {
                const response = await api.get(`${process.env.REACT_APP_URI}${historyEndpoints[pageMode]}`);
                if (response.data.success) {
                    const decryptedData = decryptPayload(response.data.encryptedData);
                    setHistoryData(decryptedData);
                }
                else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                console.error(`Error fetching rider ${pageMode} history:`, error);
            } finally {
                setLoaders(prev => ({ ...prev, fetchHistory: false }));
            }
        };
        fetchData();
    }, [pageMode]);

    // Table Header for PageMode
    const tableHeaders = {
        entry: [
            "#",
            "ID",
            "Date",
            "Category",
            "Employee",
            "Client Address",
            "Created",
        ],
        serviceFee: [
            "#",
            "ID",
            "Date",
            "Client Address",
            "Amount",
            "Created",
        ],
        expense: [
            "#",
            "ID",
            "Date",
            "Type",
            "Amount",
            "Note",
            "Created",
        ],
    };

    return (
        <>
            <div className="w-full">
                {/* Heading */}
                <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <button
                            className="md:hidden p-2 bg-white shadow rounded-lg"
                            onClick={() => setIsMobileOpen(true)}
                        >
                            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
                        </button>
                        <div>
                            <div className="flex flex-wrap items-center">
                                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                                    His
                                </h1>
                                <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                                    tory
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                <span className="text-xs font-medium text-gray-500">
                                    Last 30 Days
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            disabled={loaders.fetchHistory}
                            type="button"
                            onClick={() =>
                                setPageMode(prev =>
                                    prev === "entry"
                                        ? "serviceFee"
                                        : prev === "serviceFee"
                                            ? "expense"
                                            : "entry"
                                )
                            }
                            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-full bg-gradient-to-r from-indigo-600 to-purple-600
                            text-white shadow-md shadow-indigo-300/40 transition-all duration-200 hover:from-indigo-500 
                            hover:to-purple-500 hover:shadow-lg hover:shadow-indigo-300/50 active:scale-95"
                        >
                            {loaders.fetchHistory && (
                                <span className="h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            )}
                            {!loaders.fetchHistory && (
                                <span className="h-2 w-2 rounded-full bg-white/90 animate-pulse" />
                            )}
                            {pageMode === "entry"
                                ? "Entry"
                                : pageMode === "serviceFee"
                                    ? "Service Fee"
                                    : "Expense"}
                        </button>
                    </div>
                </div>

                <hr className="border-t border-gray-200 my-4" />

                {/* Data Display Code */}
                <div className='w-full'>
                    <div className="overflow-x-auto h-[70vh]">
                        {loaders.fetchHistory ? (
                            <div className="flex justify-center items-center h-full p-6">
                                <Loader />
                            </div>
                        ) : historyData.length === 0 ? (
                            <div className="flex justify-center items-center h-full p-6">
                                <p className="font-semibold text-gray-600 text-xl sm:text-md">
                                    No Record !!
                                </p>
                            </div>
                        ) : (
                            <>
                                <table className="w-full bg-white">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {tableHeaders[pageMode].map((header, index) => (
                                                <th
                                                    key={index}
                                                    className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {historyData.map((data, index) => (
                                            <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                                                <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                    {index + 1}
                                                </td>

                                                {pageMode === 'entry' && (
                                                    <>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.entryId}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {formatTableDateTime(data.date)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {formatLabel(data.category)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.employeeRef?.name || '-'} / {data.employeeRef?.father_husband_name || '-'}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.clientAddress || '-'}
                                                        </td>
                                                    </>
                                                )}

                                                {pageMode === 'serviceFee' && (
                                                    <>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.serviceFeeId}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {formatTableDateTime(data.date)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.clientAddress}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            PKR {formatCurrency(data.amount)}
                                                        </td>
                                                    </>
                                                )}

                                                {pageMode === 'expense' && (
                                                    <>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.expenseId}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {formatTableDateTime(data.date)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {formatLabel(data.type)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            PKR {formatCurrency(data.amount)}
                                                        </td>
                                                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                            {data.note || "-"}
                                                        </td>
                                                    </>
                                                )}

                                                <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                    {formatTableDateTime(data.createdAt)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                    </div>
                </div>
            </div>
        </>
    )
}

export default History;